import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { Typography, DialogContent, Grid2, TextField, Tooltip } from '@mui/material';
import { BatchDto } from '../../api/ClientApi';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { WarehouseClient } from '../../api/Extensions';
import { LocalizationProvider, DateTimePicker, renderTimeViewClock } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sr';
import { updateBatchForGood } from '../../redux/actions/goodsActions';
import { getSuppliers } from '../../redux/selectors/clientsSelector';
import ClientAutocomplete from '../../common/components/autocomplete/ClientAutocomplete';
import { SaveEntityStatus } from '../../redux/reducers/common';

import { FormActions, FormDialogTitle } from '../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../common/components/mobile/MobilePrimitives';

export interface BatchFormProps {
    batchId?: number;
    open: boolean;
    closeForm(): void;
    articleInfo?: string;
}

export default function BatchForm(props: BatchFormProps): ReactElement {
    const { batchId, open, closeForm, articleInfo } = props;
    const [batch, setBatch] = useState<BatchDto | undefined>(undefined);
    const [saveStatus, setSaveStatus] = useState<SaveEntityStatus>(SaveEntityStatus.None);

    const suppliers = useAppSelector(getSuppliers);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!!batchId && batchId > 0) {
            WarehouseClient.getBatchById(batchId).then((res) => {
                setBatch(res);
            });
        }
    }, [dispatch, batchId]);

    const handleInputElementChange = (event: ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        const value = target.value;
        const name = target.name;

        const newBatch = new BatchDto(batch);

        switch (name) {
            case 'purchasePricePerArticle':
                if (!isNaN(+value)) {
                    newBatch.purchasePricePerArticle = +value;
                    setBatch(newBatch);
                }
                break;
            case 'uniqueBatchId':
                newBatch.uniqueBatchId = value;
                setBatch(newBatch);
                break;
            default:
                console.log('invalid field name');
        }
    };

    const handleBatchArrivalTimeChange = (date: Dayjs | null) => {
        const newBatch = new BatchDto(batch);
        newBatch.batchArrivalTime = date ? date.toDate() : undefined;
        setBatch(newBatch);
    };

    const handleSelectSupplier = (supplierId?: number) => {
        const supplier = suppliers?.find((s) => s.id === supplierId);

        const newBatch = new BatchDto(batch);
        newBatch.supplier = supplier;
        newBatch.supplierId = supplierId;
        setBatch(newBatch);
    };

    const handleSave = () => {
        if (isEverythingOk) {
            if (!!batch.id && batch.id > 0) {
                setSaveStatus(SaveEntityStatus.Pending);
                WarehouseClient.updateBatch(batch.id, batch).then(
                    (res) => {
                        dispatch(updateBatchForGood(res));
                        setSaveStatus(SaveEntityStatus.Saved);
                        closeForm();
                    },
                    () => {
                        setSaveStatus(SaveEntityStatus.Error);
                    },
                );
            } else {
                alert('Došlo je do greške');
            }
        }
    };

    const isEverythingOk = !!batch && !!batch.batchArrivalTime && !!batch.uniqueBatchId && batch.uniqueBatchId !== '';

    return (
        <ResponsiveDialog maxWidth="md" open={open} onClose={closeForm}>
            <FormDialogTitle>Roba - izmena</FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2 container spacing={2} style={{ maxWidth: 1000, margin: 'auto' }}>
                    <Grid2 size={{ xs: 12 }}>
                        <Typography variant="body1">
                            Roba: <span style={{ fontWeight: 'bold' }}>{articleInfo}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            name="purchasePricePerArticle"
                            label="Cena po kom."
                            type="number"
                            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                            value={!!batch?.purchasePricePerArticle ? batch?.purchasePricePerArticle : ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <Tooltip title={!!batch?.supplier && !!batch?.supplier.name ? batch.supplier.name : ''}>
                            <div>
                                <ClientAutocomplete
                                    clients={suppliers ?? []}
                                    selectClient={handleSelectSupplier}
                                    selectedClientId={batch?.supplier?.id}
                                />
                            </div>
                        </Tooltip>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <TextField
                            error={!!batch?.uniqueBatchId && batch?.uniqueBatchId !== '' ? false : true}
                            required
                            fullWidth
                            name="uniqueBatchId"
                            label="Br. fakture"
                            value={!!batch?.uniqueBatchId ? batch?.uniqueBatchId : ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr">
                            <DateTimePicker
                                label="Ulazno vreme"
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                }}
                                value={batch?.batchArrivalTime ? dayjs(batch.batchArrivalTime) : null}
                                onChange={(value) => handleBatchArrivalTimeChange(value)}
                            />
                        </LocalizationProvider>
                    </Grid2>
                    <FormActions
                        onCancel={closeForm}
                        onSubmit={handleSave}
                        submitLabel="Sačuvaj"
                        disabled={!isEverythingOk || saveStatus === SaveEntityStatus.Pending}
                        pending={saveStatus === SaveEntityStatus.Pending}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
